/**
 * The tutor (doc 19) — the game teaching itself while being played.
 *
 * The prime directive: **the tutor never touches the simulation.** It reads
 * `SimState` and the drained event stream, and its only power over the game is
 * refusing to forward a command while a gate is open. It cannot spawn, grant,
 * heal or slow anything. That keeps the sim pure (doc 12), keeps replays
 * deterministic, and makes the invariant testable — a run with the tutor armed
 * produces the same hashes as a run without it.
 *
 * Ten scheduled cards, two conditional, one concept each. Two of them are
 * gates: they stay until their lesson is performed, and while one is open the
 * taught action is the only command that passes.
 */

import onboardingJson from '../content/onboarding.json' with { type: 'json' };
import type { Profile, Tutorial } from '../meta/types.ts';
import type { Sim } from '../sim/sim.ts';
import type { Command, SimEvent } from '../sim/types.ts';

export type GateId = 'g1' | 'g2';
export type TargetId = 'veins' | 'shoal';

export interface CardDef {
  id: string;
  trigger: string;
  /** Which phase the card is allowed to appear in (doc 19 §Pacing rules). */
  phase: 'build' | 'combat' | 'any';
  gate: GateId | null;
  target: TargetId | null;
  /** Conditional cards are re-tested at the moment they would be shown. */
  conditional?: boolean;
  title: string;
  body: string;
}

/** The UI facts a card can depend on — the sim has no opinion about these. */
export interface TutorView {
  selectedStructId: number;
}

export interface NudgeCard {
  id: string;
  title: string;
  body: string;
  gate: GateId | null;
  target: TargetId | null;
  /** Which shoal the G2 pulse points at, resolved when the card is shown. */
  shoalId: string | null;
}

const CARDS = onboardingJson.cards as CardDef[];
const SPACING_MS = onboardingJson.spacingSeconds * 1000;
const AUTO_DISMISS_MS = onboardingJson.autoDismissSeconds * 1000;
const RELEASE_AFTER = onboardingJson.releaseAfterDismissals;
const GATES = onboardingJson.gates;

export function newTutorial(): Tutorial {
  return { seen: [], released: [], done: false };
}

/** Alloy banked before TOWERS LEARN is worth saying (doc 05 tier-1 cost). */
const UPGRADE_HINT_ALLOY = 15;
/** Doc 19 card 5: the war chest is the ammo bar at the whistle. */
const AMMO_LOW_FRACTION = 0.25;
/** Doc 19 card 6: armour has to bite a few times before it is a lesson. */
const ARMOUR_HITS = 5;
/** Doc 14 gives a shoal three patience pips; this is the last one. */
const LAST_PIP = 1;

export class Tutor {
  private readonly state: Tutorial;
  /** Ids waiting for their phase and the 8 s spacing clock. */
  private readonly queue: string[] = [];
  private current: NudgeCard | null = null;
  private shownAt = 0;
  private lastInput = 0;
  private lastRetired = -SPACING_MS;
  private dismissals = 0;
  private armourHits = 0;

  constructor(state: Tutorial) {
    this.state = state;
  }

  /**
   * Doc 19 §Lifecycle: armed on the first run of a colony, retired for good
   * the moment D1 falls. Everything else — the harness, a second run, a run on
   * D2 — gets no tutor at all, which is why this returns null rather than an
   * inert object.
   */
  static armFor(profile: Profile | null, depth: number): Tutor | null {
    if (!profile || depth !== 1) return null;
    const t = (profile.tutorial ??= newTutorial());
    if (t.done || profile.runs > 0) return null;
    return new Tutor(t);
  }

  get card(): NudgeCard | null {
    return this.current;
  }

  /** The gate blocking commands right now, if any. */
  get gate(): GateId | null {
    const g = this.current?.gate ?? null;
    return g && !this.state.released.includes(g) ? g : null;
  }

  /** Doc 19: null passes the command through, a string is the toast instead. */
  allows(cmd: Command): string | null {
    const gate = this.gate;
    if (!gate) return null;
    const allow = GATES[gate].allows;
    const [kind, arg] = allow.split(':');
    if (cmd.type !== kind) return GATES[gate].refusal;
    if (arg !== undefined && cmd.type === 'place' && cmd.def !== arg) return GATES[gate].refusal;
    return null;
  }

  /** Any deliberate player input — it keeps a non-gate card on screen. */
  noteInput(now: number): void {
    this.lastInput = now;
  }

  /**
   * Dismiss the card on screen: a click on it, ESC, or the auto-retire clock.
   * Returns a toast when the second dismissal releases a gate (doc 19's escape
   * hatch, §B9 dev-owned).
   */
  dismiss(): string | null {
    const card = this.current;
    if (!card) return null;
    if (card.gate) {
      this.dismissals++;
      if (this.dismissals < RELEASE_AFTER) return null; // one dismissal is an accident
      this.state.released.push(card.gate);
      this.retire(card, this.lastInput);
      return onboardingJson.releaseLine;
    }
    this.retire(card, this.lastInput);
    return null;
  }

  private retire(card: NudgeCard, now: number): void {
    if (!this.state.seen.includes(card.id)) this.state.seen.push(card.id);
    this.current = null;
    this.dismissals = 0;
    this.lastRetired = now;
  }

  /**
   * One pass over the drained batch, once per frame. Enqueues whatever became
   * true, closes a gate whose lesson was performed, retires a card whose six
   * seconds are up, and promotes the next card when the rules allow it.
   */
  observe(sim: Sim, events: SimEvent[], view: TutorView, now: number): void {
    if (this.state.done) return;
    this.count(sim, events);

    for (const def of CARDS) {
      if (this.state.seen.includes(def.id)) continue;
      if (this.queue.includes(def.id) || this.current?.id === def.id) continue;
      if (this.fires(def, sim, events, view)) this.queue.push(def.id);
    }

    // a gate closes by being obeyed, never by the clock
    const open = this.current?.gate;
    if (open && this.closed(open, sim, events)) this.retire(this.current!, now);

    if (this.current && !this.current.gate && now - Math.max(this.shownAt, this.lastInput) >= AUTO_DISMISS_MS) {
      this.retire(this.current, now);
    }

    if (!this.current) this.promote(sim, events, view, now);

    // doc 19: D1 falling retires the whole sequence for this colony, for good
    if (events.some((e) => e.type === 'victory')) {
      this.state.done = true;
      this.current = null;
    }
  }

  /** Counters that need every batch, not just the batch a card is waiting on. */
  private count(sim: Sim, events: SimEvent[]): void {
    for (const ev of events) {
      if (ev.type !== 'enemyHit') continue;
      const e = sim.state.enemies.find((x) => x.id === ev.enemyId);
      if (e && e.armor > 0) this.armourHits++;
    }
  }

  private promote(sim: Sim, events: SimEvent[], view: TutorView, now: number): void {
    if (now - this.lastRetired < SPACING_MS) return; // never two cards as a lecture
    for (let k = 0; k < this.queue.length; k++) {
      const def = CARDS.find((c) => c.id === this.queue[k])!;
      if (def.phase !== 'any' && def.phase !== sim.phase) continue;
      // a conditional whose situation resolved itself was never worth mentioning
      if (def.conditional && !this.fires(def, sim, events, view)) {
        this.queue.splice(k, 1);
        k--;
        continue;
      }
      this.queue.splice(k, 1);
      this.current = {
        id: def.id,
        title: def.title,
        body: def.body,
        gate: def.gate && !this.state.released.includes(def.gate) ? def.gate : null,
        target: def.target,
        shoalId: def.target === 'shoal' ? (nearestShoal(sim) ?? null) : null,
      };
      this.shownAt = now;
      this.lastInput = now;
      this.dismissals = 0;
      return;
    }
  }

  private closed(gate: GateId, sim: Sim, events: SimEvent[]): boolean {
    if (gate === 'g1') return sim.state.structures.some((s) => s.def === 'ore_rig' && s.depositTiles.length > 0);
    return events.some((e) => e.type === 'pulled') || sim.state.waves.length > 0;
  }

  private fires(def: CardDef, sim: Sim, events: SimEvent[], view: TutorView): boolean {
    const st = sim.state;
    switch (def.trigger) {
      case 'firstBuild':
        return sim.phase === 'build';
      case 'rigPlaced':
        return events.some((e) => e.type === 'placed' && e.def === 'ore_rig') || rigOnOre(sim);
      case 'firstTower':
        return st.structures.some((s) => s.kind === 'tower') && rigOnOre(sim);
      case 'firstClear':
        return st.fightsCleared > 0;
      case 'ammoLow':
        return sim.phase === 'combat' && st.resources.ammo < AMMO_LOW_FRACTION * sim.storageCap;
      case 'armourBites':
        return this.armourHits >= ARMOUR_HITS;
      case 'towerSelected': {
        const sel = st.structures.find((s) => s.id === view.selectedStructId);
        return sel?.kind === 'tower' && st.resources.alloy >= UPGRADE_HINT_ALLOY;
      }
      case 'lastPip':
        // doc 14 gives every shoal three pips; the card is worth saying on the last
        return st.rim.some((s) => s.state === 'circling' && s.patience === LAST_PIP);
      case 'snapjawDue': {
        // Doc 19 says "the last uncleared Act 1 shoal" — but a player who saves
        // the miniboss for last is exactly the player the card cannot help; they
        // are already cornered into it. The one who needs telling is the one
        // still picking snacks halfway through the act, so that is when it fires.
        const boss = st.rim.find((s) => s.boss === 'miniboss');
        return (
          st.act === 1 &&
          boss?.state === 'circling' &&
          st.fightsCleared >= Math.floor(sim.scenario.shoals.length / 2)
        );
      }
      case 'act2':
        return events.some((e) => e.type === 'act2') || st.act > 1;
      case 'offGrid':
        return [...sim.status.values()].includes('offgrid');
      case 'brownout':
        return events.some((e) => e.type === 'brownout' && e.on);
      default:
        return false;
    }
  }
}

/**
 * Doc 19's G2 row says "a tower exists AND the rig is running". Measured on the
 * real map, not one of D1's four veins sits inside the Core's radius 5 — the
 * first rig a player ever builds is ALWAYS unplugged until they add a pylon, and
 * gating the Lure lesson on watts would hold the game's signature system hostage
 * to a card the player has not been shown yet. Standing on ore is the lesson;
 * the pylon is the `offgrid` card's job.
 */
function rigOnOre(sim: Sim): boolean {
  return sim.state.structures.some((s) => s.def === 'ore_rig' && s.depositTiles.length > 0);
}

/** The shoal G2 points at: whatever is closest to arriving on the rim. */
function nearestShoal(sim: Sim): string | undefined {
  const circling = sim.state.rim.filter((s) => s.state === 'circling');
  if (circling.length === 0) return undefined;
  return circling.reduce((best, s) => (s.menace < best.menace ? s : best)).id;
}
